import { LANGUAGES } from '@app/core/constants/language.constants';

export const COURSE_INTENTS = {
  KNOWLEDGE_TRANSFER: 'knowledge_transfer',
  COMPLIANCE_AWARENESS: 'compliance_awareness',
  SKILLS_BUILDING: 'skills_building',
  ONBOARDING: 'onboarding',
  CERTIFICATION_PREP: 'certification_prep',
  AI_DECIDE: 'ai_decide',
} as const;

export type CourseIntent = (typeof COURSE_INTENTS)[keyof typeof COURSE_INTENTS];

export const TARGET_AUDIENCES = {
  NEW_TO_TOPIC: 'new_to_topic',
  SOME_EXPERIENCE: 'some_experience',
  EXPERTS: 'experts',
} as const;

export type TargetAudience =
  (typeof TARGET_AUDIENCES)[keyof typeof TARGET_AUDIENCES];

export interface OptionConfig<T extends string = string> {
  readonly id: T;
  readonly titleKey: string;
  readonly descriptionKey: string;
  readonly icon: string;
}

export const COURSE_INTENT_OPTIONS: readonly OptionConfig<CourseIntent>[] = [
  {
    id: COURSE_INTENTS.KNOWLEDGE_TRANSFER,
    titleKey: 'guidedCourseCreation.intent.knowledgeTransfer.title',
    descriptionKey: 'guidedCourseCreation.intent.knowledgeTransfer.description',
    icon: 'auto_stories',
  },
  {
    id: COURSE_INTENTS.COMPLIANCE_AWARENESS,
    titleKey: 'guidedCourseCreation.intent.complianceAwareness.title',
    descriptionKey:
      'guidedCourseCreation.intent.complianceAwareness.description',
    icon: 'shield',
  },
  {
    id: COURSE_INTENTS.SKILLS_BUILDING,
    titleKey: 'guidedCourseCreation.intent.skillsBuilding.title',
    descriptionKey: 'guidedCourseCreation.intent.skillsBuilding.description',
    icon: 'build',
  },
  {
    id: COURSE_INTENTS.ONBOARDING,
    titleKey: 'guidedCourseCreation.intent.onboarding.title',
    descriptionKey: 'guidedCourseCreation.intent.onboarding.description',
    icon: 'person_add',
  },
  {
    id: COURSE_INTENTS.CERTIFICATION_PREP,
    titleKey: 'guidedCourseCreation.intent.certificationPrep.title',
    descriptionKey: 'guidedCourseCreation.intent.certificationPrep.description',
    icon: 'workspace_premium',
  },
  {
    id: COURSE_INTENTS.AI_DECIDE,
    titleKey: 'guidedCourseCreation.intent.aiDecide.title',
    descriptionKey: 'guidedCourseCreation.intent.aiDecide.description',
    icon: 'smart_toy',
  },
];

export const TARGET_AUDIENCE_OPTIONS: readonly OptionConfig<TargetAudience>[] =
  [
    {
      id: TARGET_AUDIENCES.NEW_TO_TOPIC,
      titleKey: 'guidedCourseCreation.audience.newToTopic.title',
      descriptionKey: 'guidedCourseCreation.audience.newToTopic.description',
      icon: 'face',
    },
    {
      id: TARGET_AUDIENCES.SOME_EXPERIENCE,
      titleKey: 'guidedCourseCreation.audience.someExperience.title',
      descriptionKey:
        'guidedCourseCreation.audience.someExperience.description',
      icon: 'hail',
    },
    {
      id: TARGET_AUDIENCES.EXPERTS,
      titleKey: 'guidedCourseCreation.audience.experts.title',
      descriptionKey: 'guidedCourseCreation.audience.experts.description',
      icon: 'work',
    },
  ];

export interface GuidedCourseCreationFormValue {
  topic: string;
  language: LANGUAGES;
  intent: CourseIntent;
  audience: TargetAudience[];
}

export interface CourseModuleOutline {
  name: string;
  moduleType: string;
  estimatedTimeMinutes: number;
  whatToCover: string;
  iconPath?: string;
}

export interface CourseSectionOutline {
  headline: string;
  modules: CourseModuleOutline[];
  expanded: boolean;
}

export interface CourseOutline {
  title: string;
  description: string;
  totalTimeMinutes: number;
  sections: CourseSectionOutline[];
}

export const MOCK_COURSE_OUTLINE: CourseOutline = {
  title: 'Introduction to GDPR and Data Privacy for Employees',
  description:
    'Understanding data privacy principles, employee responsibilities, handling personal data, and responding to data subject requests in everyday work situations.',
  totalTimeMinutes: 90,
  sections: [
    {
      headline: 'Getting Started',
      expanded: true,
      modules: [
        {
          name: 'Course Overview',
          moduleType: 'Content page',
          estimatedTimeMinutes: 10,
          whatToCover:
            "Write a concise text module outlining the regulation's background, the course structure, and what learners will be able to do by the end.",
        },
        {
          name: 'Welcome Video',
          moduleType: 'Interactive video',
          estimatedTimeMinutes: 10,
          whatToCover:
            'Record a short talking-head video explaining why data privacy matters, what GDPR means for employees specifically, and how to get the most out of the course.',
        },
      ],
    },
    {
      headline: 'Policies & Compliance',
      expanded: true,
      modules: [
        {
          name: 'Policy Documents',
          moduleType: 'Content page',
          estimatedTimeMinutes: 10,
          whatToCover:
            "Attach the company's data protection policy, a GDPR quick-reference card, and a one-page summary of employee obligations under the regulation.",
        },
        {
          name: 'Compliance Requirements',
          moduleType: 'File',
          estimatedTimeMinutes: 10,
          whatToCover:
            "Write a text module covering the six lawful bases for processing data, employee dos and don'ts, how to handle a data subject request, and what to do if a breach occurs.",
        },
        {
          name: 'Case Studies',
          moduleType: 'Interactive video',
          estimatedTimeMinutes: 10,
          whatToCover:
            'Record an interactive video walking through two or three realistic workplace situations — such as receiving a data access request or accidentally sharing personal data — and ask learners to decide what to do at key moments.',
        },
      ],
    },
    {
      headline: 'Wrap Up',
      expanded: true,
      modules: [
        {
          name: 'Course Summary',
          moduleType: 'Content page',
          estimatedTimeMinutes: 10,
          whatToCover:
            'Write a short module summarising the key rules, linking back to the policy documents, and telling learners where to go if they have questions or need to report an incident.',
        },
        {
          name: 'Additional Resources',
          moduleType: 'File',
          estimatedTimeMinutes: 10,
          whatToCover:
            "Compile a PDF with links to the ICO website, the full GDPR regulation text, the company's DPO contact, and recommended external guides on data privacy best practices.",
        },
      ],
    },
  ],
};
